interface Plan {
  name: string;
  price: string;
  unit: string;
  paymentTerms: string;
  description: string;
  features: string[];
  delivery: string;
  highlighted: boolean;
  popular: boolean;
}

const PLANS: Plan[] = [
  {
    name: "GEO Upgrade",
    price: "1 200",
    unit: "€ HT",
    paymentTerms: "Forfait, payable 100 % au démarrage",
    description:
      "Pour site déjà sain techniquement. On garde votre design, on ajoute la couche LLM-readiness.",
    features: [
      "llms.txt + llms-full.txt",
      "Carte topique 9 cellules (Topical Ownership)",
      "FAQ extractive Question-H2 (40 mots)",
      "JSON-LD Organisation, FAQ, Article",
      "Ouverture aux 14 crawlers IA majeurs",
      "Validation via re-audit gratuit après mise en ligne",
    ],
    delivery: "Livré en 5 jours ouvrés",
    highlighted: false,
    popular: false,
  },
  {
    name: "Refonte Essentielle",
    price: "2 900",
    unit: "€ HT",
    paymentTerms: "Forfait, payable 50 % au démarrage",
    description:
      "Pour entreprise de service jusqu'à 7 pages. Site sain, rapide, GEO-ready dès la mise en ligne.",
    features: [
      "Refonte design + intégration Astro",
      "SEO 2026 (Core Web Vitals validés)",
      "GEO complet (llms.txt, topical map, FAQ schema)",
      "1 an d'hébergement Cloudflare offert",
      "Formation 1 h à l'admin du site",
      "30 jours de support technique",
    ],
    delivery: "Livré en 5 jours ouvrés",
    highlighted: true,
    popular: false,
  },
  {
    name: "Refonte E-commerce",
    price: "5 900",
    unit: "€ HT",
    paymentTerms: "Forfait, payable 40 % au démarrage",
    description:
      "Pour boutique en ligne ou site complexe jusqu'à 20 pages. Catalogue, paiement, multi-langues.",
    features: [
      "Tout l'offre Essentielle, plus :",
      "Catalogue produits + paiement Stripe",
      "Gestion stocks + commandes",
      "GEO Premium (pages /ai/ dédiées, Product schema)",
      "SEO multilingue FR + EN",
      "90 jours de support technique",
    ],
    delivery: "Livré en 5 jours ouvrés",
    highlighted: true,
    popular: true,
  },
];

export function Pricing() {
  return (
    <section id="pricing" className="py-24 px-6 bg-slate-950 border-t border-slate-800">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">
            Tarifs HT — sans surprise
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            3 offres claires. Pas de "ça dépend".
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
            <strong className="text-white">L'audit gratuit qualifie d'abord votre besoin.</strong>{" "}
            Si une intervention est recommandée, voici nos forfaits HT à délai
            engagé — devis ferme sous 48 h après le cadrage.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`relative p-8 rounded-2xl border transition-all duration-300 ${
                plan.popular
                  ? "bg-slate-800/80 border-blue-500/50 shadow-xl shadow-blue-500/10"
                  : "bg-slate-800/50 border-slate-700/50 hover:border-blue-500/30"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 text-xs font-semibold text-white bg-blue-600 rounded-full shadow-lg shadow-blue-500/30">
                    Le plus demandé
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-xl text-slate-400 ml-1">{plan.unit}</span>
                </div>
                <p className="mt-2 text-xs text-slate-500">{plan.paymentTerms}</p>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                  {plan.description}
                </p>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-xs font-semibold text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-full">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {plan.delivery}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm text-slate-300">
                    <svg className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#cta"
                className={`block w-full text-center py-3 px-6 rounded-xl font-semibold text-sm transition-all duration-200 ${
                  plan.popular
                    ? "bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 hover:-translate-y-0.5"
                    : "bg-slate-700/50 text-slate-200 hover:bg-slate-700 hover:text-white border border-slate-600"
                }`}
              >
                Recevoir mon audit gratuit
              </a>
            </div>
          ))}
        </div>

        {/* Anchoring block — make our prices feel 3× cheaper */}
        <div className="mt-12 max-w-4xl mx-auto p-6 rounded-2xl bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border border-blue-500/30">
          <p className="text-center text-slate-200 leading-relaxed">
            <span className="inline-block mr-2 text-xl" aria-hidden>💡</span>
            <strong className="text-white">À titre de comparaison :</strong> une refonte équivalente
            en agence parisienne coûte <strong className="text-white">8 000 à 15 000 € HT</strong> sur
            <strong className="text-white"> 6 à 12 semaines</strong> — nous livrons en{" "}
            <strong className="text-white">5 jours ouvrés</strong> à tarif fixe. Notre différence :
            un agent IA fait 80 % de l'audit, l'équipe fondatrice de Levier IA fait
            100 % du conseil et de la prod.
          </p>
        </div>

        {/* Sur-mesure escape hatch — smaller, no price competition with main tiers */}
        <div className="mt-8 max-w-4xl mx-auto p-6 rounded-xl bg-slate-800/40 border border-slate-700/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h4 className="text-base font-semibold text-white">
              Projet plus complexe ?
            </h4>
            <p className="text-sm text-slate-400 mt-1">
              Intranet, SaaS, multi-langues, intégration CRM — réponse personnalisée sous 24 h, par Yoann directement.
            </p>
          </div>
          <a
            href="mailto:contact@levier-ia.fr?subject=Projet%20sur-mesure"
            className="shrink-0 px-5 py-2.5 text-sm font-semibold text-slate-200 bg-slate-700/50 hover:bg-slate-700 hover:text-white rounded-lg border border-slate-600 transition-all whitespace-nowrap"
          >
            Décrire mon projet →
          </a>
        </div>
      </div>
    </section>
  );
}
